/**
 * Settings → Limits page (tenancy/v1@1.2.0).
 *
 * PM-D39 introduced this as the Administration → "AI & limits" page; FB-19
 * (final) moved it to a plain "Limits" leaf under the Settings sidebar group
 * (route `/settings/limits`). The old `/admin/ai-limits` route now redirects
 * here. The tenant_admin gate is unchanged — the route still lives inside the
 * shared `RequireRole role="tenant_admin"` subtree.
 *
 * A tenant_admin sets the per-tenant **Max AI models per project** cap here.
 * The field:
 *   - READS the current value via `useTenantSettingsQuery()`
 *     (`GET /api/tenancy/v1/settings` → `{ maxAiModelsPerProject: int|null }`).
 *   - SAVES via `useUpdateTenantSettingsMutation()` (`PUT /settings`).
 *   - A blank input = **unlimited** (`null`); a number ≥ 1 = the cap.
 *
 * The cap is enforced server-side in projects/v1 (create + PUT ai-models 422
 * over-limit) — this field is the admin control + the source the create
 * wizard / project-detail AI-models panel read to cap the selector (UX guard).
 *
 * Route `/settings/limits` lives under the same tenant_admin-gated
 * `RequireRole` subtree as `/admin/*` (the BE re-asserts tenant_admin on
 * PUT), so a non-admin never reaches this page (lands on ForbiddenPage via
 * RequireRole). The Settings → Limits sidebar leaf is likewise admin-only.
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeading } from "@aeriosiq/ui";
import { ToastProvider, useToast } from "../account/ToastContext.js";
import {
  useTenantSettingsQuery,
  useUpdateTenantSettingsMutation,
} from "./hooks.js";

export function TenantSettingsPage(): ReactElement {
  return (
    <ToastProvider>
      <TenantSettingsPageInner />
    </ToastProvider>
  );
}

/** Settings value (int ≥ 1, or null = unlimited) → the text field's string. */
function toFieldValue(v: number | null | undefined): string {
  return typeof v === "number" ? String(v) : "";
}

function TenantSettingsPageInner(): ReactElement {
  const { toast } = useToast();
  const settings = useTenantSettingsQuery();
  const update = useUpdateTenantSettingsMutation();

  // Local editable string (blank = unlimited). Seeded from the loaded value
  // and re-synced whenever the persisted value changes (load + after save).
  const persisted = settings.data?.maxAiModelsPerProject ?? null;
  const [draft, setDraft] = useState<string>("");
  useEffect(() => {
    if (settings.isSuccess) setDraft(toFieldValue(persisted));
  }, [settings.isSuccess, persisted]);

  // Parse the draft into the wire value. "" → null (unlimited). A non-empty
  // value must be an integer ≥ 1, else the field is invalid (Save disabled).
  const trimmed = draft.trim();
  const parsedNum = Number(trimmed);
  const parsed: number | null = trimmed === "" ? null : parsedNum;
  const invalid =
    trimmed !== "" && (!Number.isInteger(parsedNum) || parsedNum < 1);

  const dirty = toFieldValue(persisted) !== trimmed;

  function onSave(): void {
    if (invalid) return;
    update.mutate(
      { maxAiModelsPerProject: parsed },
      {
        onSuccess: () => {
          toast("AI model limit saved.", "success");
        },
        onError: () => {
          toast("Couldn't save the AI model limit — try again.", "error");
        },
      },
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      <Box sx={{ mb: 3 }}>
        <PageHeading>Limits</PageHeading>
      </Box>

      {/* R0627-10 — the page itself stretches to the full canvas (Q-WIDTH,
          no centred container); the settings card stays a sane width so the
          single short field isn't marooned across an ultrawide row. */}
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 720 }}>
        <Typography variant="h6" gutterBottom>
          Max AI models per project
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The most AI models a project may select. Leave blank for unlimited.
          New tenants default to 3. This is enforced when a project saves its AI
          models.
        </Typography>

        {settings.isPending ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : settings.isError ? (
          <Alert severity="error">
            Couldn&apos;t load the tenant settings.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <TextField
              type="number"
              size="small"
              label="Max AI models per project"
              placeholder="Unlimited"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
              }}
              error={invalid}
              helperText={
                invalid
                  ? "Enter a whole number of 1 or more, or leave blank for unlimited."
                  : "Blank = unlimited."
              }
              slotProps={{
                htmlInput: {
                  min: 1,
                  step: 1,
                  "aria-label": "Max AI models per project",
                },
                inputLabel: { shrink: true },
              }}
              sx={{ maxWidth: 280 }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={onSave}
                disabled={!dirty || invalid || update.isPending}
              >
                {update.isPending ? "Saving…" : "Save"}
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
